import React, { useState, useEffect } from 'react';
import { 
  User,
  Calendar,
  Download,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  CreditCard,
  DollarSign,
  Receipt,
  Wallet,
  FileText,
  Eye,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  BookOpen,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

// Função auxiliar para formatar itens das vendas
const formatSaleItems = (items) => {
  if (!items) return [];
  
  try {
    // Se items já é um array, processar diretamente
    let itemsArray = Array.isArray(items) ? items : [];
    
    // Se items é uma string JSON, fazer parse
    if (typeof items === 'string') {
      try {
        itemsArray = JSON.parse(items);
      } catch (e) {
        return [items]; // Retorna array com a string original se não conseguir fazer parse
      }
    }
    
    // Se não é array após o processamento, retornar array com string
    if (!Array.isArray(itemsArray)) {
      return [String(items)];
    }
    
    // Formatar cada item com mais detalhes
    return itemsArray.map(item => {
      if (typeof item === 'object' && item.quantity) {
        const quantity = item.quantity;
        const unitPrice = item.unit_price || 0;
        const totalPrice = item.total_price || (quantity * unitPrice);
        
        // Se temos nome do produto, usar ele
        if (item.product_name) {
          return `${quantity}x ${item.product_name} (R$ ${unitPrice.toFixed(2)}) = R$ ${totalPrice.toFixed(2)}`;
        }
        
        // Senão, usar informações básicas
        return `${quantity}x R$ ${unitPrice.toFixed(2)} = R$ ${totalPrice.toFixed(2)}`;
      }
      return String(item);
    });
    
  } catch (error) {
    console.error('Erro ao formatar itens:', error);
    return [String(items)];
  }
};

// Função auxiliar para buscar nomes dos produtos e enriquecer itens
const enrichItemsWithProductNames = async (items, companyId) => {
  if (!items || !Array.isArray(items)) return items;
  
  try {
    // Coletar todos os product_ids únicos
    const productIds = items
      .filter(item => item.product_id && !item.product_name)
      .map(item => item.product_id);
    
    if (productIds.length === 0) return items;
    
    // Buscar produtos por IDs usando dataHelpers
    const products = await dataHelpers.getProducts(companyId);
    
    if (!products || products.length === 0) return items;
    
    // Criar mapa de id -> nome
    const productMap = {};
    products.forEach(product => {
      productMap[product.id] = product.name;
    });
    
    // Enriquecer itens com nomes dos produtos
    return items.map(item => ({
      ...item,
      product_name: item.product_name || productMap[item.product_id] || `Produto ${item.product_id?.substring(0, 8)}...`
    }));
    
  } catch (error) {
    console.error('Erro ao enriquecer itens com nomes dos produtos:', error);
    return items;
  }
};

const StudentExtractPage = () => {
  const { company, person } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlyInvoices, setMonthlyInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [typeFilter, setTypeFilter] = useState('all');
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' ou 'monthly'

  useEffect(() => {
    if (company?.id) {
      loadStudents();
    }
  }, [company]);

  // Recarregar dados quando filtros mudarem
  useEffect(() => {
    if (selectedStudent) {
      if (activeTab === 'transactions') {
        loadStudentTransactions(selectedStudent.id);
      } else {
        loadStudentMonthlyInvoices(selectedStudent.id);
      }
    }
  }, [dateFilter, typeFilter, activeTab]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const studentsData = await dataHelpers.getStudents(company.id);
      setStudents(studentsData || []);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  };
  const loadStudentTransactions = async (studentId) => {
    try {
      setLoadingTransactions(true);
      
      // Carregar extrato completo do aluno usando a função específica
      const extractData = await dataHelpers.getStudentFinancialExtract(
        company.id, 
        studentId,
        {
          startDate: dateFilter.startDate || null,
          endDate: dateFilter.endDate || null,
          type: typeFilter
        }
      );
      
      // Processar transações - os itens já vêm enriquecidos do backend
      const formattedTransactions = (extractData || []).map(transaction => {
          return {
            id: transaction.id,
            type: transaction.type,
            description: transaction.description,
            amount: parseFloat(transaction.amount) || 0,
            balance_after: parseFloat(transaction.balance_after) || 0,
            payment_method: transaction.payment_method,
            created_at: transaction.created_at,
            details: {
              ...transaction.details,
              items: transaction.items || [], // Itens já enriquecidos com nomes dos produtos
              sale_details: transaction.sale_details,
              operator: transaction.creator?.name || 'Sistema',
              category: transaction.category?.name
            }
          };
        });
      
      setTransactions(formattedTransactions);
      
      // Dados do aluno não são retornados pela função atual
      // if (extractData.student && extractData.student.id === selectedStudent?.id) {
      //   setSelectedStudent(prev => ({
      //     ...prev,
      //     ...extractData.student,
      //     name: extractData.student.people?.name || extractData.student.name
      //   }));
      // }
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Erro ao carregar extrato');
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadStudentMonthlyInvoices = async (studentId) => {
    try {
      setLoadingTransactions(true);
      
      // Buscar todas as faturas de mensalidade da empresa
      const allInvoices = await dataHelpers.getMonthlyInvoices(company.id);
      
      // Filtrar apenas as faturas do aluno selecionado
      const studentInvoices = allInvoices.filter(invoice => 
        invoice.monthly_subscribers?.students?.id === studentId
      );
      
      // Aplicar filtros de data se existirem
      let filteredInvoices = studentInvoices;
      if (dateFilter.startDate || dateFilter.endDate) {
        filteredInvoices = studentInvoices.filter(invoice => {
          const invoiceDate = new Date(invoice.due_date);
          const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
          const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;
          
          if (startDate && invoiceDate < startDate) return false;
          if (endDate && invoiceDate > endDate) return false;
          return true;
        });
      }
      
      setMonthlyInvoices(filteredInvoices);
    } catch (error) {
      console.error('Error loading student monthly invoices:', error);
      toast.error('Erro ao carregar faturas de mensalidade');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    if (activeTab === 'transactions') {
      loadStudentTransactions(student.id);
    } else {
      loadStudentMonthlyInvoices(student.id);
    }
    setShowStudentModal(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (selectedStudent) {
      if (tab === 'transactions') {
        loadStudentTransactions(selectedStudent.id);
      } else {
        loadStudentMonthlyInvoices(selectedStudent.id);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Função para obter estatísticas das mensalidades
  const getMonthlyInvoicesStats = () => {
    const pending = monthlyInvoices.filter(invoice => invoice.status === 'pending').length;
    const overdue = monthlyInvoices.filter(invoice => 
      invoice.status === 'pending' && new Date(invoice.due_date) < new Date()
    ).length;
    const paid = monthlyInvoices.filter(invoice => invoice.status === 'paid').length;
    
    return { pending, overdue, paid, total: monthlyInvoices.length };
  };
  // Função para traduzir métodos de pagamento
  const translatePaymentMethod = (method) => {
    const translations = {
      'student_balance': 'Saldo do Aluno',
      'balance': 'Saldo',
      'cash': 'Dinheiro',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'bank_transfer': 'Transferência Bancária',
      'voucher': 'Voucher',
      'parents_portal': 'Portal dos Pais',
      'other': 'Outros'
    };
    return translations[method] || method;
  };

  const getStudentBalanceColor = (balance) => {
    return balance >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'sale':
        return <ShoppingCart className="w-5 h-5 text-red-600" />;
      case 'credit':
        return <ArrowUpCircle className="w-5 h-5 text-green-600" />;
      case 'refund':
        return <ArrowDownCircle className="w-5 h-5 text-blue-600" />;
      case 'fee':
        return <FileText className="w-5 h-5 text-orange-600" />;
      default:
        return <Receipt className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTransactionColor = (transaction) => {
    // Verificar se é recarga pela descrição como fallback
    const isRecarga = transaction.description && (
      transaction.description.includes('Recarga') || 
      transaction.description.includes('PIX') ||
      transaction.description.includes('Portal dos Pais') ||
      transaction.description.includes('Saldo inicial') ||
      transaction.description.includes('Ajuste de saldo (acréscimo)')
    );
    
    // Se tem transaction_type, usar ele
    if (transaction.transaction_type === 'credit') {
      return 'bg-green-50 border-green-200';
    }
    if (transaction.transaction_type === 'debit') {
      return 'bg-red-50 border-red-200';
    }
    
    // Fallback baseado no tipo de transação
    switch (transaction.type) {
      case 'sale':
        return 'bg-red-50 border-red-200';
      case 'credit':
        return 'bg-green-50 border-green-200';
      case 'refund':
        return 'bg-blue-50 border-blue-200';
      case 'fee':
        return 'bg-orange-50 border-orange-200';
      default:
        // Fallback final: se é recarga, é verde (+), senão é vermelho (-)
        return isRecarga ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
    }
  };
  
  const getTransactionValueColor = (transaction) => {
    // Verificar se é recarga pela descrição como fallback
    const isRecarga = transaction.description && (
      transaction.description.includes('Recarga') || 
      transaction.description.includes('PIX') ||
      transaction.description.includes('Portal dos Pais') ||
      transaction.description.includes('Saldo inicial') ||
      transaction.description.includes('Ajuste de saldo (acréscimo)')
    );
    
    // Se tem transaction_type, usar ele
    if (transaction.transaction_type === 'credit') {
      return 'text-green-600';
    }
    if (transaction.transaction_type === 'debit') {
      return 'text-red-600';
    }
    
    // Fallback: se é recarga, é verde (+), senão é vermelho (-)
    return isRecarga ? 'text-green-600' : 'text-red-600';
  };
  
  const getTransactionPrefix = (transaction) => {
    // Verificar se é recarga pela descrição como fallback
    const isRecarga = transaction.description && (
      transaction.description.includes('Recarga') || 
      transaction.description.includes('PIX') ||
      transaction.description.includes('Portal dos Pais') ||
      transaction.description.includes('Saldo inicial') ||
      transaction.description.includes('Ajuste de saldo (acréscimo)')
    );
    
    // Se tem transaction_type, usar ele
    if (transaction.transaction_type === 'credit') {
      return '+';
    }
    if (transaction.transaction_type === 'debit') {
      return '-';
    }
    
    // Fallback: se é recarga, é verde (+), senão é vermelho (-)
    return isRecarga ? '+' : '-';
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'sale':
        return 'Compra';
      case 'credit':
        return 'Crédito';
      case 'refund':
        return 'Estorno';
      case 'fee':
        return 'Taxa';
      default:
        return type;
    }
  };

  const filteredStudents = students.filter(student => {
    const studentName = student.people?.name || student.name || '';
    const parentName = student.person?.name || '';
    const registration = student.registration_number || '';
    
    return (
      studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registration.includes(searchTerm)
    );
  });

  const filteredTransactions = transactions.filter(transaction => {
    const matchesType = typeFilter === 'all' || transaction.type === typeFilter;
    
    let matchesDate = true;
    if (dateFilter.startDate && dateFilter.endDate) {
      const transactionDate = new Date(transaction.created_at);
      const startDate = new Date(dateFilter.startDate);
      const endDate = new Date(dateFilter.endDate);
      matchesDate = transactionDate >= startDate && transactionDate <= endDate;
    }
    
    return matchesType && matchesDate;
  });

  const calculateSummary = () => {
    const summary = {
      totalCredits: 0,
      totalDebits: 0,
      totalTransactions: filteredTransactions.length,
      currentBalance: selectedStudent?.balance || 0
    };

    filteredTransactions.forEach(transaction => {
      // Verificar se é recarga pela descrição como fallback
      const isRecarga = transaction.description && (
        transaction.description.includes('Recarga') || 
        transaction.description.includes('PIX') ||
        transaction.description.includes('Portal dos Pais') ||
        transaction.description.includes('Saldo inicial') ||
        transaction.description.includes('Ajuste de saldo (acréscimo)')
      );
      
      // Se tem transaction_type, usar ele
      if (transaction.transaction_type === 'credit') {
        summary.totalCredits += transaction.amount;
      } else if (transaction.transaction_type === 'debit') {
        summary.totalDebits += transaction.amount;
      } else {
        // Fallback: se é recarga, é crédito, senão é débito
        if (isRecarga) {
          summary.totalCredits += transaction.amount;
        } else {
          summary.totalDebits += transaction.amount;
        }
      }
    });

    return summary;
  };

  const summary = selectedStudent ? calculateSummary() : null;

  const exportExtract = () => {
    if (!selectedStudent) {
      toast.error('Selecione um aluno primeiro');
      return;
    }
    
    try {
      // Criar conteúdo HTML para impressão/PDF
      const studentName = selectedStudent.people?.name || selectedStudent.name;
      const currentDate = new Date().toLocaleDateString('pt-BR');
      const summary = calculateSummary();
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Extrato - ${studentName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .student-info { margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 5px; }
            .summary { margin-bottom: 30px; }
            .summary-item { display: inline-block; margin-right: 30px; padding: 10px; background: white; border: 1px solid #ddd; border-radius: 5px; }
            .transactions { width: 100%; border-collapse: collapse; }
            .transactions th, .transactions td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .transactions th { background-color: #f2f2f2; }
            .credit { color: green; }
            .debit { color: red; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Extrato de Movimentações</h1>
            <h2>${studentName}</h2>
            <p>Gerado em: ${currentDate}</p>
          </div>
          
          <div class="student-info">
            <h3>Informações do Aluno</h3>
            <p><strong>Nome:</strong> ${studentName}</p>
            <p><strong>Matrícula:</strong> ${selectedStudent.registration_number}</p>
            <p><strong>Turma:</strong> ${selectedStudent.class_name}</p>
            <p><strong>Responsável:</strong> ${selectedStudent.person?.name || 'Não informado'}</p>
            <p><strong>Saldo Atual:</strong> ${formatCurrency(selectedStudent.balance || 0)}</p>
          </div>
          
          <div class="summary">
            <h3>Resumo do Período</h3>
            <div class="summary-item">
              <strong>Total de Créditos:</strong><br>
              <span class="credit">${formatCurrency(summary.totalCredits)}</span>
            </div>
            <div class="summary-item">
              <strong>Total de Débitos:</strong><br>
              <span class="debit">${formatCurrency(summary.totalDebits)}</span>
            </div>
            <div class="summary-item">
              <strong>Transações:</strong><br>
              ${summary.totalTransactions}
            </div>
          </div>
          
          <h3>Histórico de Movimentações</h3>
          <table class="transactions">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Tipo</th>
                <th>Valor</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      filteredTransactions.forEach(transaction => {
        // Verificar se é recarga pela descrição como fallback
        const isRecarga = transaction.description && (
          transaction.description.includes('Recarga') || 
          transaction.description.includes('PIX') ||
          transaction.description.includes('Portal dos Pais') ||
          transaction.description.includes('Saldo inicial') ||
          transaction.description.includes('Ajuste de saldo (acréscimo)')
        );
        
        // Se tem transaction_type, usar ele
        let isCredit;
        if (transaction.transaction_type === 'credit') {
          isCredit = true;
        } else if (transaction.transaction_type === 'debit') {
          isCredit = false;
        } else {
          // Fallback: se é recarga, é crédito, senão é débito
          isCredit = isRecarga;
        }
        
        const valueClass = isCredit ? 'credit' : 'debit';
        const valuePrefix = isCredit ? '+' : '-';
        
        htmlContent += `
              <tr>
                <td>${formatDateTime(transaction.created_at)}</td>
                <td>${transaction.description}</td>
                <td>${getTypeLabel(transaction.type)}</td>
                <td class="${valueClass}">${valuePrefix}${formatCurrency(transaction.amount)}</td>
                <td>${formatCurrency(transaction.balance_after)}</td>
              </tr>
        `;
      });
      
      htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            <p>Este extrato foi gerado automaticamente pelo sistema CantinaGo</p>
            <p>Data de geração: ${new Date().toLocaleString('pt-BR')}</p>
          </div>
        </body>
        </html>
      `;
      
      // Abrir nova janela para impressão
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Dar tempo para carregar e então abrir diálogo de impressão
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success('Extrato aberto para impressão/PDF');
    } catch (error) {
      console.error('Error exporting extract:', error);
      toast.error('Erro ao exportar extrato');
    }
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Extrato do Aluno</h1>
            <p className="text-gray-600">
              Histórico detalhado de movimentações financeiras
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            {selectedStudent && (
              <Button variant="outline" onClick={exportExtract}>
                <Download className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            )}
            <Button onClick={() => setShowStudentModal(true)}>
              <User className="w-4 h-4 mr-2" />
              Selecionar Aluno
            </Button>
          </div>
        </div>

        {selectedStudent ? (
          <>
            {/* Student Info Card */}
            <Card className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedStudent.people?.name || selectedStudent.name}
                    </h2>
                    <p className="text-gray-600">
                      Matrícula: {selectedStudent.registration_number} • {selectedStudent.class_name}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        Responsável: {selectedStudent.person?.name || 'Não informado'}
                      </div>
                      {selectedStudent.person?.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {selectedStudent.person.email}
                        </div>
                      )}
                      {selectedStudent.person?.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {selectedStudent.person.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Saldo Atual</p>
                  <p className={`text-3xl font-bold ${
                    (selectedStudent.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(selectedStudent.balance || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Tipo: {selectedStudent.payment_type === 'prepaid' ? 'Pré-pago' : 'Mensalista'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100">
                      <ArrowUpCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Créditos</p>
                      <p className="text-2xl font-semibold text-green-600">
                        {formatCurrency(summary.totalCredits)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-red-100">
                      <ArrowDownCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Débitos</p>
                      <p className="text-2xl font-semibold text-red-600">
                        {formatCurrency(summary.totalDebits)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100">
                      <Receipt className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Transações</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {summary.totalTransactions}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100">
                      <Wallet className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Saldo Atual</p>
                      <p className={`text-2xl font-semibold ${
                        summary.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(summary.currentBalance)}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {activeTab === 'transactions' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Movimentação
                    </label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os tipos</option>
                      <option value="sale">Compras</option>
                      <option value="credit">Créditos</option>
                      <option value="refund">Estornos</option>
                      <option value="fee">Taxas</option>
                    </select>
                  </div>
                )}

                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setDateFilter({ startDate: '', endDate: '' });
                      setTypeFilter('all');
                    }}
                    className="w-full"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </Card>

            {/* Tabs */}
            <Card>
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  <button
                    onClick={() => handleTabChange('transactions')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'transactions'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Histórico de Movimentações
                    {activeTab === 'transactions' && filteredTransactions.length !== transactions.length && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({filteredTransactions.length} de {transactions.length})
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => handleTabChange('monthly')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm relative ${
                      activeTab === 'monthly'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="flex items-center">
                      Mensalidades
                      {(() => {
                        const stats = getMonthlyInvoicesStats();
                        if (stats.total === 0) return null;
                        
                        // Determinar cor do badge baseado no status
                        let badgeColor = 'bg-gray-100 text-gray-800'; // padrão
                        if (stats.overdue > 0) {
                          badgeColor = 'bg-red-100 text-red-800';
                        } else if (stats.pending > 0) {
                          badgeColor = 'bg-yellow-100 text-yellow-800';
                        } else {
                          badgeColor = 'bg-green-100 text-green-800';
                        }
                        
                        return (
                          <>
                            <span className={`ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              activeTab === 'monthly' ? 'bg-blue-100 text-blue-800' : badgeColor
                            }`}>
                              {stats.total}
                            </span>
                            {stats.overdue > 0 && (
                              <span className="ml-1 flex h-2 w-2" title={`${stats.overdue} fatura(s) vencida(s)`}>
                                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                              </span>
                            )}
                            {stats.overdue === 0 && stats.pending > 0 && (
                              <span className="ml-1 flex h-2 w-2" title={`${stats.pending} fatura(s) pendente(s)`}>
                                <span className="animate-pulse absolute inline-flex h-2 w-2 rounded-full bg-yellow-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </span>
                  </button>
                </nav>
              </div>
              
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : activeTab === 'transactions' ? (
                filteredTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma movimentação encontrada</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => (
                    <div key={transaction.id} className={`p-6 border-l-4 ${getTransactionColor(transaction)}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            {getTransactionIcon(transaction.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-gray-900">
                                {transaction.description}
                              </h4>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {getTypeLabel(transaction.type)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDateTime(transaction.created_at)}
                            </p>
                            
                            {/* Transaction Details */}
                            {transaction.details && (
                              <div className="mt-2 text-xs text-gray-600">
                                {transaction.type === 'sale' && transaction.details.items && (
                                  <div>
                                    <p className="mb-1">Itens:</p>
                                    {console.log('🛒 Items for transaction:', transaction.id, transaction.details.items)}
                                    {formatSaleItems(transaction.details.items).map((item, index) => (
                                      <p key={index} className="ml-2">
                                        • {item}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                {transaction.details.operator && (
                                  <p>Operador: {transaction.details.operator}</p>
                                )}
                                {transaction.details.added_by && (
                                  <p>Adicionado por: {transaction.details.added_by}</p>
                                )}
                                {transaction.details.notes && (
                                  <p>Observações: {transaction.details.notes}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className={`text-lg font-semibold ${getTransactionValueColor(transaction)}`}>
                            {getTransactionPrefix(transaction)}{formatCurrency(transaction.amount)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Saldo: {formatCurrency(transaction.balance_after)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )
              ) : (
                // Aba de Mensalidades
                monthlyInvoices.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhuma mensalidade encontrada</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Parcela
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plano
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vencimento
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Valor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pagamento
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {monthlyInvoices.map((invoice) => {
                          const isOverdue = new Date(invoice.due_date) < new Date() && invoice.status === 'pending';
                          const statusConfig = {
                            pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pendente' },
                            paid: { color: 'bg-green-100 text-green-800', text: 'Pago' },
                            overdue: { color: 'bg-red-100 text-red-800', text: 'Vencido' }
                          };
                          const status = isOverdue ? 'overdue' : invoice.status;
                          
                          return (
                            <tr key={invoice.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {invoice.installment_number}/{invoice.total_installments}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {invoice.monthly_subscribers?.monthly_plans?.name || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {formatCurrency(invoice.final_amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig[status].color}`}>
                                  {statusConfig[status].text}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString('pt-BR') : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </Card>
          </>
        ) : (
          <Card className="p-12 text-center">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Selecione um Aluno</h3>
            <p className="text-gray-500 mb-6">
              Escolha um aluno para visualizar seu extrato de movimentações financeiras
            </p>
            <Button onClick={() => setShowStudentModal(true)}>
              <User className="w-4 h-4 mr-2" />
              Selecionar Aluno
            </Button>
          </Card>
        )}
      </div>

      {/* Student Selection Modal */}
      {showStudentModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Selecionar Aluno</h3>
              <button
                onClick={() => setShowStudentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <AlertCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, responsável ou matrícula..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="overflow-y-auto max-h-96">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredStudents.length === 0 ? (
                  <div className="col-span-2 text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum aluno encontrado</p>
                  </div>
                ) : (
                  filteredStudents.map((student) => (
                    <button
                      key={student.id}
                      onClick={() => handleStudentSelect(student)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {student.people?.name || student.name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {student.class_name} • Mat: {student.registration_number}
                          </p>
                          <p className="text-sm text-gray-600">
                            Resp: {student.person?.name || 'Não informado'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            (student.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(student.balance || 0)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    
    </>
  );
};

export default StudentExtractPage;
